import { ErrorModal, useToken } from "@dexteel/mesf-core";
import {
	Button,
	Card,
	CardContent,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Snackbar,
	TextField,
	Typography,
} from "@mui/material";
import Alert from "@mui/lab/Alert";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useWorkOrdersContext } from "../../context/workOrdersContext";
import { closeWorkOrders } from "../../repositorie/WorkOrdersRepositorie";
import { cmmsTheme } from "@setup/theme/cmms-theme";
import Grid2 from "@mui/material/Grid2";

type Props = {
	show: boolean;
	onHide: (onHide: boolean) => void;
};

export const CloseGroupWorkOrdersDialog = ({ onHide, show }: Props) => {
	const [closeSuccess, setCloseSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [userInput, setUserInput] = useState<string>("");
	const { getUserName } = useToken();
	const {
		state: {
			dataToGetWorkOrders: { AssetId },
			listWorkOrderSelected,
			commentCloseGroupWO,
			dataToCloseWOGroup,
		},
		actions: { setCommentCloseGroupWO, setDataToCloseWOGroup },
	} = useWorkOrdersContext();
	const listWorkOrderSelectedCount = listWorkOrderSelected.length;

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		onHide(false);
	};

	const handleCancel = () => {
		onHide(false);
	};

	const appendComment = (newComment: string) => {
		const now = moment().format("DD/MM/YYYY HH:mm");
		let newValue = "";
		if (
			commentCloseGroupWO === "" ||
			commentCloseGroupWO === null ||
			commentCloseGroupWO === "null"
		) {
			newValue = getUserName() + " - " + now + " - " + newComment;
		} else {
			newValue = commentCloseGroupWO + "\n" + getUserName() + " - " + now + " - " + newComment;
		}
		if (newComment !== "") {
			setUserInput("");
			setCommentCloseGroupWO(newValue);
		}
	};

	const addNewComment = () => {
		appendComment(userInput);
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key === "Enter") {
			appendComment((e.target as HTMLInputElement).value);
			e.preventDefault();
		}
	};

	const closeSelectedWorkOrders = useCallback(() => {
		if (dataToCloseWOGroup) {
			setIsSubmitLoading(true);
			const errors: string[] = [];

			const promises = dataToCloseWOGroup.map((workOrder) =>
				closeWorkOrders(workOrder.data?.id, workOrder.Comments).then((res) => {
					if (!res.ok) {
						errors.push(
							"Error closing work order " + workOrder.data?.Title + " : " + res.message + ".",
						);
					}
					return res.ok;
				}),
			);

			Promise.all(promises)
				.then((results) => {
					if (results.every((result) => result === true)) {
						setTimeout(() => {
							setCloseSuccess(true);
							onHide(true);
							setIsSubmitLoading(false);
						}, 100);
					} else {
						setIsSubmitLoading(false);
						if (errors.length > 0) {
							const formattedError = errors.join("  ");
							setError(formattedError);
						}
					}
				})
				.catch((error) => {
					setIsSubmitLoading(false);
					setError(error.message);
				});
		}
	}, [dataToCloseWOGroup, listWorkOrderSelected, AssetId]);

	useEffect(() => {
		for (let i = 0; i < listWorkOrderSelected.length; i++) {
			const existingComments = listWorkOrderSelected[i].data?.Comments;
			listWorkOrderSelected[i].Comments = existingComments
				? existingComments + "\n" + commentCloseGroupWO
				: "\n" + commentCloseGroupWO;
		}
		setDataToCloseWOGroup(listWorkOrderSelected);
	}, [listWorkOrderSelected, commentCloseGroupWO, show]);

	useEffect(() => {
		if (show) {
			setCommentCloseGroupWO("");
			setUserInput("");
			setIsSubmitLoading(false);
		}
	}, [show]);

	return (
		<>
			<Dialog fullWidth maxWidth="sm" open={show} onClose={() => handleCancel()}>
				<DialogTitle>CLOSE: {listWorkOrderSelectedCount} WORK ORDERS</DialogTitle>

				<DialogContent dividers>
					{/* Comments Card */}
					<Card
						variant="outlined"
						sx={{
							backgroundColor: cmmsTheme.colors.commentsBackground,
							mb: 2,
						}}
					>
						<CardContent>
							<Typography sx={{ fontWeight: "bold", mb: 1 }}>Comments</Typography>
							<textarea
								value={commentCloseGroupWO as string}
								disabled
								style={{
									resize: "none",
									width: "100%",
									height: "150px",
									border: "none",
									backgroundColor: "transparent",
									fontSize: 14,
									fontFamily: "inherit",
								}}
							/>
						</CardContent>
					</Card>

					{/* Add Comment Row */}
					<Grid2 container spacing={2} alignItems="center">
						<Grid2 size={{ xs: 12, sm: 9 }}>
							<TextField
								variant="outlined"
								size="small"
								multiline
								maxRows={3}
								value={userInput}
								onChange={(e) => setUserInput(e.target.value)}
								label="Comment"
								fullWidth
								onKeyDown={onKeyDown}
								autoComplete="off"
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, sm: 3 }}>
							<Button
								fullWidth
								variant="contained"
								color="primary"
								onClick={addNewComment}
							>
								ADD COMMENT
							</Button>
						</Grid2>
					</Grid2>
				</DialogContent>

				<DialogActions sx={{ padding: "16px 24px" }}>
					<Grid2
						container
						spacing={2}
						justifyContent="space-between"
						sx={{ width: "100%", margin: 0 }}
					>
						<Grid2 size={{ xs: 6, sm: 3 }}>
							<Button fullWidth variant="contained" onClick={handleCancel}>
								CANCEL
							</Button>
						</Grid2>
						<Grid2 size={{ xs: 6, sm: 3 }}>
							<Button
								fullWidth
								startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
								disabled={isSubmitLoading}
								variant="contained"
								color="secondary"
								onClick={closeSelectedWorkOrders}
							>
								CLOSE ORDERS
							</Button>
						</Grid2>
					</Grid2>
				</DialogActions>
			</Dialog>

			<Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={closeSuccess} autoHideDuration={2500} onClose={handleClose}>
				<Alert severity="warning" onClose={handleClose}>
					Work order closed
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
